import { Button, H3, InputLabel, InputWithUnit, Select, TextArea, TextInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { Warning } from '@components/Common/SharedStyles';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import { Customer } from '@api/queries/customers';
import { ProductMixture } from '@api/queries/productMixtures';
import { Product } from '@api/queries/products';
import { Tank } from '@api/queries/tanks';
import { flatMap, includes, uniqBy } from 'lodash';
import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ProductType } from '~types/types';

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

const WaybillDescription = styled(TextArea)`
  height: 100px;
`;

interface ComponentProps {
  product?: ProductMixture;
  products: Product[];
  customers: Customer[];
  save: (product: ProductMixture) => void;
  close: () => void;
  remove: () => void;
  isSaving: boolean;
  tanks: Tank[];
}

type Props = ComponentProps & WithTranslation;

interface State {
  productMixture: ProductMixture;
}

class ProductMixtureEditor extends Component<Props, State> {
  constructor(props) {
    super(props);

    const product = props.product;

    this.state = {
      productMixture: {
        id: (product && product.id) || null,
        customerId: (product && product.customerId) || '',
        customerName: '',
        description: '',
        productType: ProductType.Product,
        mixtures: (product && product.mixtures) || [
          { id: null, productId: null, portation: 0, notes: null },
          { id: null, productId: null, portation: 0, notes: null }
        ],
        code: (product && product.code) || '',
        name: (product && product.name) || '',
        notes: (product && product.notes) || '',
        isEnabled: true,
        waybillDescription: (product && product.waybillDescription) || ''
      }
    };

    this.onInputChanged = this.onInputChanged.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onRemove = this.onRemove.bind(this);
  }

  public render() {
    const { code, customerId, mixtures, name, notes, waybillDescription } = this.state.productMixture;
    const mixture1 = mixtures[0];
    const mixture2 = mixtures[1];
    const { customers, products, t, isSaving, tanks } = this.props;
    const isEdit = this.props.product !== null;

    const enablesTanks = tanks.filter(i => i.isEnabled);
    const enabledProducts = products
      .filter(i => i.isEnabled)
      .filter(i => enablesTanks.some(j => Object.keys(i.tanks).indexOf(j.id) > -1));

    const customerOptions = uniqBy(
      customers
        .filter(i => includes(flatMap(enabledProducts, k => Object.keys(k.customers)), i.id))
        .map(i => ({ value: i.id, label: i.name })),
      i => i.value
    );
    const selectedCustomer = customerOptions.filter(i => i.value === customerId)[0] || null;

    const productOptions = flatMap(
      products
        .filter(i => i.isEnabled || (i.id === mixture1.productId || i.id === mixture2.productId))
        .filter(i => i.customers[selectedCustomer ? selectedCustomer.value : '']),
      i =>
        Object.keys(i.tanks).map(j => ({
          value: `${i.id}_${j}`,
          label: `${i.name} (${i.code}) (${i.tanks[j]})`
        }))
    );

    const selectedProduct1 =
      productOptions.filter(i => i.value === `${mixture1.productId}_${mixture1.tankId}`)[0] || null;

    const selectedProduct2 =
      productOptions.filter(i => i.value === `${mixture2.productId}_${mixture2.tankId}`)[0] || null;

    const isSameProduct = mixture1 && mixture2 && mixture1.productId === mixture2.productId;

    return (
      <SideForm onSubmit={this.onSave} onEscape={this.props.close}>
        {isEdit ? (
          <H3>
            {t('editing')}: <i>{this.props.product.name}</i>
          </H3>
        ) : (
          <H3>
            {t('add_new')} {t('product.mixture').toLowerCase()}
          </H3>
        )}
        <div>
          <InputLabel htmlFor="code">{t('product.code')} *</InputLabel>
          <TextInput id="code" name="code" type="text" value={code} onChange={this.onInputChanged} autoFocus />
        </div>
        <div>
          <InputLabel htmlFor="name">{t('product.name')} *</InputLabel>
          <TextInput id="name" name="name" type="text" value={name} onChange={this.onInputChanged} />
        </div>
        <div>
          <div>
            <InputLabel htmlFor="customerId">{t('entry.customer')} *</InputLabel>
            <Select
              name="customerId"
              placeholder={t('select_customer')}
              value={selectedCustomer}
              options={customerOptions}
              isDisabled={false}
              onChange={(o, a) => this.onSelectChange(a.action === 'clear' ? null : o.value, 'customerId')}
            />
          </div>
          <div>
            <InputLabel htmlFor="productId1">1. {t('entry.product')} *</InputLabel>
            <Select
              name="productId"
              inputId="productId1"
              placeholder={t('select_product')}
              value={selectedProduct1}
              options={productOptions}
              isDisabled={false}
              onChange={(o, a) => this.onMixtureChange(a.action === 'clear' ? null : o.value, 0)}
            />
          </div>
          <div>
            <InputLabel htmlFor="portation1">{t('product.portation')} *</InputLabel>
            <InputWithUnit>
              <TextInput
                name="portation"
                id="portation1"
                type="number"
                step="any"
                value={mixture1.portation}
                onChange={e => this.onDecimalChanged(e, 0)}
              />
              <span>%</span>
            </InputWithUnit>
          </div>
          <div>
            <InputLabel htmlFor="productId2">2. {t('entry.product')} *</InputLabel>
            <Select
              name="productId"
              inputId="productId2"
              placeholder={t('select_product')}
              value={selectedProduct2}
              options={productOptions}
              isDisabled={false}
              onChange={(o, a) => this.onMixtureChange(a.action === 'clear' ? null : o.value, 1)}
            />
          </div>
          {isSameProduct && <Warning>{t('product.warning_same_product_in_mixture')}</Warning>}
          <div>
            <InputLabel htmlFor="portation2">{t('product.portation')} *</InputLabel>
            <InputWithUnit>
              <TextInput
                name="portation"
                id="portation2"
                type="number"
                step="any"
                value={mixture2.portation}
                onChange={e => this.onDecimalChanged(e, 1)}
              />
              <span>%</span>
            </InputWithUnit>
          </div>
        </div>
        <div>
          <InputLabel htmlFor="notes">{t('product.notes')}</InputLabel>
          <TextArea id="notes" name="notes" value={notes} onChange={this.onInputChanged} />
        </div>
        <div>
          <InputLabel htmlFor="waybillDescription">{t('product.waybill_description')}</InputLabel>
          <WaybillDescription
            id="waybillDescription"
            name="waybillDescription"
            value={waybillDescription}
            onChange={this.onInputChanged} />
        </div>
        <SidebarButtons>
          <Button type="button" secondary={true} onClick={this.props.close}>
            {t('cancel')}
          </Button>
          <div />
          {isSaving && <OperationLoader title={t('saving') + '...'} />}
          {isEdit && (
            <RemoveButton type="button" danger={true} disabled={isSaving} onClick={this.onRemove}>
              {t('delete')}
            </RemoveButton>
          )}
          <Button type="submit" tertiary={true} disabled={!this.isValid() || isSaving}>
            {t('save')}
          </Button>
        </SidebarButtons>
      </SideForm>
    );
  }

  private onSave(event: React.FormEvent<any>) {
    event.preventDefault();
    this.props.save(this.state.productMixture);
  }

  private onInputChanged(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    this.setState({
      productMixture: { ...this.state.productMixture, [e.target.name]: e.target.value }
    });
  }

  private onSelectChange(value: string, propName: string) {
    const mixtures = [...this.state.productMixture.mixtures];

    if (propName === 'customerId') {
      mixtures[0] = { ...mixtures[0], productId: null };
      mixtures[1] = { ...mixtures[1], productId: null };
    }

    this.setState({
      ...this.state,
      productMixture: { ...this.state.productMixture, [propName]: value, mixtures }
    });
  }

  private onDecimalChanged(e: React.ChangeEvent<HTMLInputElement>, mixtureIndex: number) {
    const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;

    const mixtures = [...this.state.productMixture.mixtures];
    mixtures[mixtureIndex] = { ...mixtures[mixtureIndex], [e.target.name]: value };

    if (mixtureIndex === 0 && value != null) {
      const mixture2Value = 100 - value;
      mixtures[1] = { ...mixtures[1], [e.target.name]: mixture2Value };
    }

    if (mixtureIndex === 1 && value != null) {
      const mixture1Value = 100 - value;
      mixtures[0] = { ...mixtures[0], [e.target.name]: mixture1Value };
    }

    this.setState({
      ...this.state,
      productMixture: { ...this.state.productMixture, mixtures }
    });
  }

  private onMixtureChange(value: string, mixtureIndex: number) {
    const mixtures = [...this.state.productMixture.mixtures];

    if (value) {
      const selected = value.split('_');
      mixtures[mixtureIndex] = { ...mixtures[mixtureIndex], productId: selected[0], tankId: selected[1] };
    } else {
      mixtures[mixtureIndex] = { ...mixtures[mixtureIndex], productId: null, tankId: null };
    }

    this.setState({
      ...this.state,
      productMixture: { ...this.state.productMixture, mixtures }
    });
  }

  private isValid() {
    const { code, name, customerId, mixtures } = this.state.productMixture;
    return (
      code.trim().length > 0 &&
      name.trim().length > 0 &&
      customerId &&
      mixtures[0].productId &&
      mixtures[1].productId &&
      mixtures[0].productId !== mixtures[1].productId &&
      mixtures[0].portation > 0 &&
      mixtures[1].portation > 0
    );
  }

  private onRemove() {
    if (confirm(this.props.t('product.confirm_delete'))) {
      this.props.remove();
    }
  }
}

export default withTranslation()(ProductMixtureEditor) as React.ComponentType<ComponentProps>;
